import type {
  ReportQuery,
  ReportRecordsRequest,
  ReportTileChart
} from "@gettactic/api";
import type { ReportForm } from "./pickForm";

/**
 * A story is an Explore view someone wants to hand over: the question, how
 * it is drawn, and the drill trail that led to it. It travels as one URL
 * parameter so a pasted link lands on exactly that view.
 */
export type Story = {
  query: ReportQuery | null;
  records: ReportRecordsRequest | null;
  form: ReportForm;
  trail: ReportQuery[];
  /** What the chart remembers besides its form: a second measure, an order, hidden members. */
  chart?: ReportTileChart | null;
};

const toBase64Url = (s: string): string =>
  btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const fromBase64Url = (s: string): string =>
  decodeURIComponent(escape(atob(s.replace(/-/g, "+").replace(/_/g, "/"))));

export const encodeStory = (story: Story): string =>
  toBase64Url(JSON.stringify(story));

/** Null when the parameter is not a story this build understands. */
export const decodeStory = (param: string | null): Story | null => {
  if (!param) {
    return null;
  }
  try {
    const parsed = JSON.parse(fromBase64Url(param)) as Partial<Story>;
    if (!parsed || (!parsed.query && !parsed.records)) {
      return null;
    }
    return {
      query: parsed.query ?? null,
      records: parsed.records ?? null,
      form: parsed.form ?? "auto",
      trail: Array.isArray(parsed.trail) ? parsed.trail : [],
      chart: parsed.chart ?? null
    };
  } catch {
    return null;
  }
};

/** The URL that opens Explore on this story: same page, same origin. */
export const storyUrl = (story: Story): string =>
  `${window.location.origin}${window.location.pathname}?story=${encodeStory(story)}#tab-explore`;

/** The Explore query-string for a story, for links built elsewhere. */
export const storyParam = (story: Story): string =>
  `?story=${encodeStory(story)}#tab-explore`;
